import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/apiClient';
import type { DataResponse } from '../types/api';
import { useAuth } from './AuthContext';
import { profileQueryKey } from './useProfile';

/** Known keys — mirrors discovr-backend's internal/users.knownSkippableKeys allowlist. */
export type SkippableKey = 'brand_brain_welcome';

/**
 * Records when the current user skipped a prompt that comes back after a while
 * (POST /profile/skipped-announcements/{key}), so a skip holds per USER across
 * devices. The time comes back as the profile's skipped_announcements; how long
 * a skip lasts is the caller's call. Invalidates the profile on success.
 */
export function useMarkAnnouncementSkipped() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (key: SkippableKey) =>
      apiClient
        .post<DataResponse<{ skipped: boolean }>>(`/profile/skipped-announcements/${key}`)
        .then((r) => r.data.data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: profileQueryKey(user?.uid) });
    },
    // Here, not on mutate: a skip usually navigates away, and mutate's own callbacks don't run after an unmount.
    onError: (e, key) => console.warn(`could not save skipping ${key}`, e),
  });
}
